// Role-based access control
// Roles: admin, staff, user, corporate_admin, ma, finance, strategy, viewer
//
// Present navbar tabs:
//   Explore  → Use Case Discovery (open), Startup Search, Trends, Saved Use Cases (open), VC Intelligence
//   Track    → Engagement Repo, M&A Intelligence
//   Control  → Startup Management, User Management

export type AppRole =
  | "admin"
  | "staff"
  | "user"
  | "corporate_admin"
  | "ma"
  | "finance"
  | "strategy"
  | "viewer";

interface TabPermissions {
  // Explore dropdown
  startups: boolean;        // Startup Search
  vcIntel: boolean;         // VC Intelligence
  trends: boolean;          // Trends
  // Track dropdown
  engagementRepo: boolean;  // Engagement Repo
  acquisitions: boolean;    // M&A Intelligence
  // Control Center
  controlCenter: boolean;   // Startup Management (add/edit)
  manageUsers: boolean;     // User Management
  deleteStartups: boolean;  // Delete startups
  // Always-visible tabs (home, about, use-case-discovery, saved-use-cases)
  // are not gated by permissions — shown to all logged-in users
}

const ALL_ACCESS: TabPermissions = {
  startups: true,
  vcIntel: true,
  trends: true,
  engagementRepo: true,
  acquisitions: true,
  controlCenter: false,
  manageUsers: false,
  deleteStartups: false,
};

const ROLE_PERMISSIONS: Record<AppRole, TabPermissions> = {
  // Full access — all tabs + startup management + user management
  admin: { ...ALL_ACCESS, controlCenter: true, manageUsers: true, deleteStartups: true },

  // All tabs + startup management (add/edit only, no delete or user management)
  staff: { ...ALL_ACCESS, controlCenter: true, manageUsers: false, deleteStartups: false },

  corporate_admin: { ...ALL_ACCESS, controlCenter: true, manageUsers: true, deleteStartups: true },
  user: { ...ALL_ACCESS },

  // Startup Search, M&A Intelligence, Engagement Repo
  ma: {
    startups: true,
    vcIntel: false,
    trends: false,
    engagementRepo: true,
    acquisitions: true,
    controlCenter: false,
    manageUsers: false,
    deleteStartups: false,
  },

  // VC Intelligence, Trends, Engagement Repo
  finance: {
    startups: false,
    vcIntel: true,
    trends: true,
    engagementRepo: true,
    acquisitions: false,
    controlCenter: false,
    manageUsers: false,
    deleteStartups: false,
  },

  // Startup Search, Trends, Engagement Repo
  strategy: {
    startups: true,
    vcIntel: false,
    trends: true,
    engagementRepo: true,
    acquisitions: false,
    controlCenter: false,
    manageUsers: false,
    deleteStartups: false,
  },

  // Engagement Repo only (read-only) — Home & About always visible
  viewer: {
    startups: false,
    vcIntel: false,
    trends: false,
    engagementRepo: true,
    acquisitions: false,
    controlCenter: false,
    manageUsers: false,
    deleteStartups: false,
  },
};

export function getPermissions(role: string | null): TabPermissions {
  if (!role) return ALL_ACCESS;
  return ROLE_PERMISSIONS[role as AppRole] ?? ALL_ACCESS;
}


export const ROLE_LABELS: Record<AppRole, string> = {
  admin: "Super Admin",
  staff: "Staff",
  corporate_admin: "Corporate Admin",
  user: "User",
  ma: "M&A Team",
  finance: "Finance Team",
  strategy: "Strategy Team",
  viewer: "Viewer",
};

export const ALL_ROLES: AppRole[] = [
  "admin",
  "staff",
  "corporate_admin",
  "user",
  "ma",
  "finance",
  "strategy",
  "viewer",
];
